import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Radio } from '@startuptools/ui/Radio';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { GqlWarrantProgram, GqlWarrantProgramKind } from '../../../../../graphql/base-types.graphql';
import { useWpSetLanguageMutation } from './WarrantProgramStepper.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';

type FormValues = {
  secondaryLang: 'true' | 'false';
};

export const WpStepLanguages = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'companyId' | 'secondaryLang' | 'warrantKind'>;
}) => {
  const [doSetLanguage, { loading }] = useWpSetLanguageMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      secondaryLang: `${warrantProgram.secondaryLang}`,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSetLanguage({
        variables: {
          companyId: warrantProgram.companyId,
          input: { id: warrantProgram.id, primaryLang: true, secondaryLang: values.secondaryLang === 'true' },
        },
      });
    } catch (e) {
      captureException(e);
      toast({
        severity: 'danger',
        title: <Trans id="Could not save share price" />,
        description: <Trans id="Could not save language, please try again and contact support if problem persists" />,
      });
    }
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          {warrantProgram.warrantKind === GqlWarrantProgramKind.Employee ? (
            <Trans id="Select Langage" />
          ) : (
            <Trans id="In which languages should the documents be generated?" />
          )}
        </Typography>
        {warrantProgram.warrantKind === GqlWarrantProgramKind.Employee && (
          <Alert>
            <Trans id="Here you choose which language the agreements shall have (language settings for the meeting itself is chosen in the meeting)." />
          </Alert>
        )}

        <Controller
          control={control}
          name="secondaryLang"
          render={({ field }) => (
            <RadioGroup {...field}>
              <Radio value={false} label={<Trans id="Swedish Only" />} />
              <Radio value label={<Trans id="Swedish and English" />} />
            </RadioGroup>
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
